/**
 * Pre-login organization discovery.
 *
 * The SPA must send an `organization` to Auth0 before it can log in
 * (organization_usage=require), but on a first visit it doesn't know the user's
 * tenant yet. Rather than duplicate Auth0's mapping client-side, it asks the
 * backend with the full email: most domains map 1:1 via Auth0's discovery
 * config, but shared domains (bestseller.com) route by the user's org
 * membership, which needs the address, not just the domain. See
 * `GET /v2/auth/resolve-org` in the API.
 *
 * Lives in `@appretio/shared` (not `@appretio/auth`) so it can type its result
 * off the generated API schema — `@appretio/auth` can't depend on
 * `@appretio/api` without forming a package cycle.
 */

import type { components } from "@appretio/api";

// Empty base = same-origin (dev proxies /auth to the API; prod serves it).
const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * A tenant resolved for an email. The `id` (`org_…`) is passed to Auth0 as
 * `organization`. Aliased to the generated `UserOrganization` schema so the
 * shape can't drift from the API.
 */
export type ResolvedOrg = components["schemas"]["UserOrganization"];

/**
 * Resolve the candidate tenant(s) for an email address. Usually one org (the
 * caller redirects straight to it); a shared domain with no prior membership
 * returns several so the caller can show a one-time picker. Returns `[]` when
 * the email maps to no tenant (unknown). Throws on network / unexpected server
 * errors so the caller can offer a retry.
 */
export async function resolveOrgForEmail(
  email: string
): Promise<ResolvedOrg[]> {
  const normalized = email.trim().toLowerCase();
  if (!normalized.includes("@")) {
    return [];
  }

  const response = await fetch(
    `${apiBase}/v2/auth/resolve-org?email=${encodeURIComponent(normalized)}`
  );
  if (response.status === 404) {
    return [];
  }
  if (!response.ok) {
    throw new Error(`Organization lookup failed (status ${response.status}).`);
  }
  const body = (await response.json()) as { result?: ResolvedOrg[] };
  return body.result ?? [];
}

/**
 * Resolve a tenant *name* (a link's `?org=` hint) to its org. Returns `null`
 * when the name maps to no served tenant — the hint is advisory, so callers
 * fall back to the normal login flow. Throws on network / unexpected server
 * errors.
 */
export async function resolveOrgByName(
  name: string
): Promise<ResolvedOrg | null> {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const response = await fetch(
    `${apiBase}/v2/auth/resolve-org?org=${encodeURIComponent(normalized)}`
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Organization lookup failed (status ${response.status}).`);
  }
  const body = (await response.json()) as { result?: ResolvedOrg[] };
  return body.result?.[0] ?? null;
}
